import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { PARAM_FILTER, QUERY_KEY } from '../../../constants'

import { toast } from '../../../utils'

import { getCashTransaction } from '../../../api/my'
import type { GetCashTransactionParams } from '../../../api/my'

import { CenterWrap, ColumnItem, RowBetween } from '../../../components/common'
import { Text } from '../../../components/text'
import { Spinner } from '../../../components/spinner/Spinner'
import { InfinityScroll } from '../../../components/pagingList/InfinityScroll'
import { SlideFilter } from '../../../components/paramOption/SlideFilter'
import { Sort } from '../../../components/paramOption/Sort'

import { Color } from '../../../theme/color'
import type { ColorKeys } from '../../../theme/color'

type TransactionType = GetCashTransactionParams['transactionType']
type SortType = GetCashTransactionParams['sort']

const FILTER_LIST: { type: TransactionType; title: string }[] = [
  { type: undefined, title: '전체' },
  { type: PARAM_FILTER.SALE, title: '청약 신청' },
  { type: PARAM_FILTER.DEPOSIT, title: '예치금 충전' },
  { type: PARAM_FILTER.WITHDRAWAL, title: '예치금 출금' },
  { type: PARAM_FILTER.SETTLEMENT, title: '정산' },
  { type: PARAM_FILTER.UNALLOCATED, title: '환불' },
  { type: PARAM_FILTER.REFUND, title: '청약 취소' },
]

const SORT_LIST: { type: SortType; title: string }[] = [
  { type: 'createdDate,desc', title: '최근 거래순' },
  { type: 'createdDate,asc', title: '오래된 거래순' },
]

export function Transaction() {
  const [sort, setSortType] = useState<SortType>('createdDate,desc')
  const [transactionType, setTransactionType] = useState<TransactionType | undefined>()

  const { data, isPending, isError, error, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEY.cashTransaction, { transactionType, sort }],
    queryFn: async ({ pageParam = 0 }) =>
      await getCashTransaction({ offset: pageParam, size: 50, transactionType, sort }),
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.offset + lastPage.size),
    initialPageParam: 0,
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.list).flat(),
    }),
  })

  useEffect(() => {
    if (isError && error) {
      if (isAxiosError(error)) toast(error.response?.data.message, error)
    }
  }, [isError, error])

  if (isPending) {
    return (
      <CenterWrap>
        <Spinner />
      </CenterWrap>
    )
  }

  const isEmpty = isError || !data || (!isPending && !data.pages.length)

  return (
    <>
      <FilterContainer>
        <SlideFilter<GetCashTransactionParams['transactionType']>
          filterType={transactionType}
          filterList={FILTER_LIST}
          setFilterType={setTransactionType}
        />
      </FilterContainer>
      <SortContainer>
        <Sort<GetCashTransactionParams['sort']> sortType={sort} sortList={SORT_LIST} setSortType={setSortType} />
      </SortContainer>
      {isEmpty ? (
        <EmptyBox>
          <Text size={14} lineHeight={22} color="text_secondary" $textAlign="center">
            조회 가능한 내역이 없습니다.
          </Text>
        </EmptyBox>
      ) : (
        <InfinityScroll length={data.pages.length} isLast={!hasNextPage} next={fetchNextPage}>
          <ColumnItem>
            {data.pages.map(
              ({ id, createdDate, transactionType, purchaseTypeLabel, orderSequence, amount, balance, hasFee }) => {
                const YMD = dayjs(createdDate).format('YY.MM.DD')
                const HM = dayjs(createdDate).format('HH:mm')
                const isWithdrawal = String(amount).includes('-')
                const textColor: ColorKeys = isWithdrawal ? 'primary_5' : 'error'
                const isTransaction = (['SALE', 'REFUND'] as TransactionType[]).includes(transactionType)

                return (
                  <BreakdownContainer key={id}>
                    <TransactionContainer>
                      <ColumnItem $gap={6}>
                        <Text size={10} color="text_secondary">
                          {YMD}
                        </Text>
                        <Text size={10} color="text_secondary">
                          {HM}
                        </Text>
                      </ColumnItem>
                      <ColumnItem $gap={4}>
                        <RowBetween>
                          <Text size={16} weight={500}>
                            {purchaseTypeLabel}
                          </Text>
                          <ColumnItem>
                            <RowBetween>
                              <Text size={18} weight={600} color={textColor} $textAlign="right">
                                {isWithdrawal ? amount.toLocaleString() : `+${amount.toLocaleString()}`}원
                              </Text>
                            </RowBetween>
                          </ColumnItem>
                        </RowBetween>
                        <AmountInfoBox>
                          {isTransaction && <Text size={10}>거래ID: {orderSequence}</Text>}
                          <AmountDescription>
                            {hasFee && (
                              <Text size={10} color="text_secondary">
                                (수수료 포함)
                              </Text>
                            )}
                            <Text size={10} color="text_secondary">
                              {balance.toLocaleString()}원
                            </Text>
                          </AmountDescription>
                        </AmountInfoBox>
                      </ColumnItem>
                    </TransactionContainer>
                  </BreakdownContainer>
                )
              },
            )}
          </ColumnItem>
        </InfinityScroll>
      )}
    </>
  )
}

const EmptyBox = styled.div`
  text-align: center;
  width: 100%;
  padding: 120px 0;
`

const FilterContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 16px 0;
  background-color: ${Color.white};
`

const SortContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 16px;
  padding-bottom: 16px;
`

const BreakdownContainer = styled.div`
  padding: 0 24px;
`

const TransactionContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 32px;
  padding: 16px 0;
  border-bottom: 1px solid ${Color.line};
  > *:first-child {
    flex-shrink: 0;
  }
  > *:last-child {
    width: 100%;
  }
`

const AmountInfoBox = styled(RowBetween)`
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  > *:first-child {
    flex-grow: 1;
    width: 100%;
  }
`

const AmountDescription = styled(ColumnItem)`
  flex-grow: 0;
  flex-shrink: 0;
  align-items: flex-end;
  gap: 6px;
`
